import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo, { ORG_SCHEMA, breadcrumbSchema } from "@/components/Seo";
import Editable from "@/components/Editable";
import { Link } from "react-router-dom";
import { GraduationCap, Heart, Trophy, ArrowRight, Microphone } from "@phosphor-icons/react";
import EditableSection from "@/components/blocks/EditableSection";

const PHOTO = "https://customer-assets.emergentagent.com/job_bark-academy-9/artifacts/3iytpcdr_Chralotte%20bryan%20therapy%20dogs.jpg";

const approachItems = [
  { id: "approach.1", icon: Heart, title: "Non-aversive only", desc: "Fear-free, evidence-based training — no fear, no force." },
  { id: "approach.2", icon: GraduationCap, title: "Puppy specialist", desc: "A focus on puppy specialist work — alongside therapy dogs, behaviour modification and adult dog development." },
  { id: "approach.3", icon: Trophy, title: "Real-life outcomes", desc: "Skills that translate to walks, visitors and everyday life." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo title="About the Puppl team | Puppl" description="Meet the Puppl team — Sunshine Coast puppy trainer and dog consultant with 1000+ puppies trained and assessor for the Wishlist Therapy Dog Program at SCUH." canonical={typeof window !== "undefined" ? window.location.origin + "/about" : ""} schema={[ORG_SCHEMA, breadcrumbSchema([{ name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" }, { name: "About", url: typeof window !== "undefined" ? window.location.origin + "/about" : "/about" }])]} />
      <PublicHeader />
      <EditableSection slug="about" />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <img loading="lazy" src={PHOTO} alt="the Puppl team with one of her therapy dogs" className="rounded-3xl w-full aspect-square object-cover shadow-lg" />
          <div className="absolute -bottom-6 -right-6 bg-[#f38838] text-white rounded-2xl p-6 shadow-xl hidden md:block">
            <Editable id="about.stat.number" as="div" className="font-heading text-3xl font-bold" defaultHtml="1000+" />
            <Editable id="about.stat.label" as="div" className="text-sm opacity-90" defaultHtml="puppies trained" />
          </div>
        </div>
        <div>
          <Editable id="about.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#f38838] mb-3" defaultHtml="About" />
          <Editable id="about.heading" as="h1" className="font-heading text-4xl lg:text-5xl font-bold text-stone-900 mb-6 leading-tight" defaultHtml="Hi, I&#39;m the team." />
          <Editable id="about.body.p1" as="p" className="text-stone-700 text-lg leading-relaxed mb-4" defaultHtml="I&#39;m the Sunshine Coast Puppy Trainer &amp; Dog Consultant behind <em>Puppl</em>. I specialise in puppy development and puppy training — because raising the best adult dog requires the right training, and it starts <strong>now</strong>." />
          <Editable id="about.body.p2" as="p" className="text-stone-700 text-lg leading-relaxed mb-4" defaultHtml="I&#39;ve trained over 1000 puppies across the Sunshine Coast through puppy school, teenage puppy school, private consults, walk &amp; trains, home check-ins and specialist scent detection classes." />
          <Editable id="about.body.p3" as="p" className="text-stone-700 text-lg leading-relaxed mb-8" defaultHtml="I&#39;m also the assessor for the <strong>Wishlist Therapy Dog Program</strong> at Sunshine Coast University Hospital — preparing well-suited dogs and their humans for therapy work." />
          <div className="flex flex-wrap gap-3">
            <Link to="/services" className="btn-primary inline-flex items-center gap-2">
              <Editable id="about.cta.primary" inline as="span" defaultHtml="See Services" /> <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/contact" className="btn-secondary inline-flex items-center gap-2">
              <Editable id="about.cta.secondary" inline as="span" defaultHtml="Contact the team" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12 bg-stone-100">
        <div className="max-w-6xl mx-auto">
          <Editable id="about.approach.heading" as="h2" className="font-heading text-3xl font-bold text-stone-900 mb-10 text-center" defaultHtml="My approach" />
          <div className="grid md:grid-cols-3 gap-6">
            {approachItems.map((f, i) => (
              <div key={i} className="card-pp p-6">
                <div className="w-12 h-12 rounded-xl bg-[#f38838]/15 flex items-center justify-center mb-4">
                  <f.icon size={26} weight="duotone" className="text-[#f38838]" />
                </div>
                <Editable id={`about.${f.id}.title`} as="h3" className="font-heading font-semibold text-xl text-stone-900 mb-2" defaultHtml={f.title} />
                <Editable id={`about.${f.id}.desc`} as="p" className="text-sm text-stone-600 leading-relaxed" defaultHtml={f.desc} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
